import React, { useMemo, useState } from "react";
import "../styles/OrderHistory.css";
import "../styles/AdminUsersOverview.css";
import AdminUserPairsModal from "./AdminUserPairsModal";

const AdminUsersOverview = ({
  users = [],
  loading,
  error,
  onRefresh,
  onEdit,
  onConsole,
  onRestart,
  onStart,
  onStop,
  onDelete,
  onMarkPaid,
  busyDeleteEmail,
  onApplyReplacement,
  onApplyAddition,
  onRemovePair,
  onApplyAllSuggestions,
}) => {
  const [pairsModalUser, setPairsModalUser] = useState(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const PAGE_SIZE = 8;

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((item) => {
      const pairsText = (Array.isArray(item?.trading_pairs) ? item.trading_pairs : [])
        .map((entry) => entry?.symbol || "")
        .join(" ");
      const haystack = [
        item?.username,
        item?.full_name,
        String(item?.trade_mode || ""),
        item?.symbol,
        pairsText,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [users, search]);

  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pagedUsers = useMemo(
    () => filteredUsers.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredUsers, safePage]
  );

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const goToPage = (target) => setPage(Math.max(1, Math.min(target, totalPages)));

  const formatCurrency = (value, digits = 2) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "N/A";
    return `$${numeric.toFixed(digits)}`;
  };

  const formatNumber = (value, digits = 4) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return "N/A";
    return numeric.toFixed(digits);
  };

  const buildBalancesSummary = (coins = [], totalFallback = null, isSim = false) => {
    if (!coins.length) {
      const numeric = Number(totalFallback);
      if (Number.isFinite(numeric) && numeric > 0) {
        return `${isSim ? "SIM" : "USDT"}: ${formatCurrency(numeric, 2)}`;
      }
      return "Sin saldos";
    }
    return coins
      .slice(0, 3)
      .map((coin) => `${coin.coin}: ${formatCurrency(coin.usdt_value, 2)}`)
      .join(" · ");
  };

  const buildOrdersSummary = (orders = []) => {
    if (!orders.length) return "0";
    const sample = orders
      .slice(0, 2)
      .map((order) => `${order.side || "—"} ${formatNumber(order.size, 4)} @ ${formatCurrency(order.price, 4)}`)
      .join(" | ");
    return `${orders.length} (${sample})`;
  };

  const formatTime = (value) => {
    if (!value) return "—";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    return date.toLocaleTimeString();
  };

  const formatChatIds = (raw) => {
    if (!raw) return "";
    if (Array.isArray(raw)) {
      return raw.filter(Boolean).join(", ");
    }
    return String(raw);
  };

  const toDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date;
  };

  const formatShortDate = (date) =>
    date
      ? date.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" })
      : null;

  const buildSubscriptionMeta = (user) => {
    if (user?.is_admin) {
      return {
        badge: "ACTIVA · ADMIN",
        tone: "positive",
        detail: "Cuenta admin exenta de suscripción",
        pending: false,
      };
    }
    const isSim = String(user?.trade_mode || "").trim().toUpperCase() !== "REAL";
    if (isSim) {
      return {
        badge: "Sin suscripción",
        tone: "neutral",
        detail: "Cuenta SIM — no aplica",
        pending: false,
      };
    }
    const sub = user?.sub || {};
    const end = toDate(sub.period_end || user?.subscription_period_end);
    const start = toDate(sub.period_start || user?.subscription_period_start);
    const blocked = sub.blocked ?? Boolean(user?.subscription_blocked);
    const pending = sub.payment_pending ?? Boolean(user?.subscription_payment_pending);
    const autoRejected = user?.subscription_auto_rejected_at;
    const method = sub.payment_method || user?.subscription_payment_method;
    const pendingAmount =
      Number(sub.payment_pending_amount) > 0
        ? Number(sub.payment_pending_amount)
        : Number(user?.subscription_pending_amount) > 0
        ? Number(user?.subscription_pending_amount)
        : Number(user?.subscription_due_amount) > 0
        ? Number(user?.subscription_due_amount)
        : null;
    const due = Number(sub.due_amount) > 0 ? Number(sub.due_amount) : null;
    const amountText =
      pendingAmount != null
        ? `$${pendingAmount.toFixed(2)}`
        : due != null
        ? `$${due.toFixed(2)}`
        : "";
    const fmtEnd = formatShortDate(end);
    const daysLeft =
      sub.days_left !== undefined && sub.days_left !== null
        ? Number(sub.days_left)
        : end
        ? Math.ceil((end.getTime() - Date.now()) / 86400000)
        : null;
    const dueText = due != null ? ` · debe ${due.toFixed(2)} USDT` : "";

    if (blocked) {
      return { badge: "BLOQUEADA", tone: "negative", detail: `${fmtEnd ? `Vence ${fmtEnd}` : ""}${dueText}`, pending: false };
    }
    if (autoRejected) {
      return { badge: "RECHAZADA", tone: "negative", detail: `Pago rechazado${fmtEnd ? ` · vence ${fmtEnd}` : ""}`, pending: false };
    }
    if (pending) {
      const extra = [amountText, method].filter(Boolean).join(" ");
      return { badge: "PAGO PENDIENTE", tone: "warning", detail: `${extra ? `${extra} · ` : ""}${fmtEnd ? `vence ${fmtEnd}` : ""}`, pending: true };
    }
    if (!end) return { badge: "Sin suscripción", tone: "neutral", detail: "—", pending: false };
    if (daysLeft === null || daysLeft < 0) {
      return { badge: "VENCIDA", tone: "negative", detail: `${fmtEnd ? `Venció ${fmtEnd}` : ""}${dueText}`, pending: false };
    }
    if (daysLeft <= 3) {
      return { badge: `POR VENCER (${daysLeft}d)`, tone: "warning", detail: `${fmtEnd ? `Vence ${fmtEnd}` : ""}${dueText}`.trim(), pending: false };
    }
    return { badge: `Activa · ${daysLeft}d`, tone: "positive", detail: `${fmtEnd}${dueText}`.trim(), pending: false };
  };

  const getUserPairs = (user) => (Array.isArray(user?.trading_pairs) ? user.trading_pairs : []);

  const buildPairsSummary = (user) => {
    const totalPairs = getUserPairs(user).length;
    return totalPairs > 0 ? `${totalPairs} pares configurados` : user?.symbol || "Sin definir";
  };

  const modalOpen = Boolean(pairsModalUser);
  const selectedModalUser = useMemo(() => {
    if (!pairsModalUser?.username) return null;
    return users.find((item) => item?.username === pairsModalUser.username) || pairsModalUser;
  }, [pairsModalUser, users]);

  const getBotMeta = (statusRaw, enabled = true) => {
    if (!enabled) return { label: "OFF", tone: "neutral" };
    const status = String(statusRaw || "STOPPED").toUpperCase().trim();
    if (status === "RUNNING") return { label: "RUNNING", tone: "positive" };
    if (status === "STARTING") return { label: "STARTING", tone: "warning" };
    if (status === "ERROR") return { label: "ERROR", tone: "negative" };
    return { label: status || "STOPPED", tone: "neutral" };
  };

  const getCredentialMeta = (
    credentialStatus,
    fallbackError,
    configured = false,
    tradeMode = "REAL"
  ) => {
    const code = String(credentialStatus?.code || "").toUpperCase().trim();
    const label = credentialStatus?.label;
    const tone = credentialStatus?.tone;
    if (label && tone) return { label, tone };
    const errorText = String(fallbackError || "").toUpperCase();
    if (String(tradeMode || "").toUpperCase() === "SIM") {
      return { label: "Simulación", tone: "warning" };
    }
    if (!configured) return { label: "Sin llaves", tone: "neutral" };
    if (errorText.includes("APIKEY_EXPIRED")) return { label: "Llave vencida", tone: "negative" };
    if (errorText.includes("INVALID_APIKEY")) return { label: "Llave inválida", tone: "negative" };
    if (code === "OK") return { label: "Pionex OK", tone: "positive" };
    if (code === "ISSUE") return { label: "Revisar API", tone: "warning" };
    return { label: "Pionex OK", tone: "positive" };
  };

  const getCardState = (botEnabled, user) => {
    if (user?.error) return "error";
    if (!botEnabled) return "off";
    const status = String(user?.bot?.status || "").toUpperCase().trim();
    if (status === "ERROR") return "error";
    if (status === "RUNNING") return "running";
    if (status === "STARTING") return "starting";
    return "off";
  };

  const botStatusIcon = (label = "") => {
    const value = String(label).toUpperCase().trim();
    if (value === "RUNNING") return "▶";
    if (value === "STARTING") return "◐";
    if (value === "ERROR") return "✖";
    if (value === "OFF") return "⏸";
    return "•";
  };

  const worstGateTone = (gate) => {
    if (!gate) return null;
    const total = Number(gate.evaluados || 0);
    if (total <= 0) return null;
    const ratio = Number(gate.pasaron || 0) / total;
    if (ratio >= 0.8) return "positive";
    if (ratio >= 0.5) return "warning";
    return "negative";
  };

  return (
    <section className="order-history admin-users-overview admin-users-cards-view">
      <header className="history-header">
        <div>
          <h3 className="history-title">Usuarios Pionex</h3>
          <p className="history-subtitle">Estado del bot, suscripción, balances y órdenes abiertas.</p>
        </div>
        <div className="history-toolbar admin-users-toolbar">
          <div className="admin-users-search">
            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              placeholder="Buscar por email, nombre, modo, par..."
            />
            {search ? (
              <button type="button" className="admin-users-search-clear" onClick={() => { setSearch(""); setPage(1); }}>
                ✕
              </button>
            ) : null}
          </div>
          <span className="admin-users-count">{filteredUsers.length} usuario(s)</span>
          <button type="button" className="history-refresh-btn" onClick={onRefresh} disabled={loading}>
            {loading ? "Actualizando..." : "Actualizar"}
          </button>
        </div>
      </header>

      {error ? <div className="history-error">{error}</div> : null}

      {loading && users.length === 0 ? (
        <div className="history-loading">
          <div className="history-spinner" />
          <div>Cargando usuarios...</div>
        </div>
      ) : users.length === 0 ? (
        <div className="history-empty">Sin usuarios con información.</div>
      ) : filteredUsers.length === 0 ? (
        <div className="history-empty">Sin usuarios que coincidan con la búsqueda.</div>
      ) : (
        <>
          <div className="admin-users-grid">
          {pagedUsers.map((user, idx) => {
            const balances = user?.balances || {};
            const coins = balances?.coins || [];
            const openOrders = user?.open_orders || [];
            const botEnabled = user?.bot_enabled !== false;
            const isDeleting = busyDeleteEmail && user?.username === busyDeleteEmail;
            const tradeMode = String(user?.trade_mode || "").toUpperCase() || "—";
            const isSim = tradeMode === "SIM";
            const pairsLabel = buildPairsSummary(user);
            const pairCount = getUserPairs(user).length;
            const requiresKeys = !isSim;
            const canStart =
              !botEnabled &&
              Boolean(user?.symbol || (Array.isArray(user?.trading_pairs) && user.trading_pairs.length)) &&
              (requiresKeys ? Boolean(user?.pionex_configured) : true);
            const simBalance = Number(user?.bot?.sim_balance);
            const hasSimBalance = Number.isFinite(simBalance);
            const rawTotal = Number(balances?.total_usdt);
            const totalBalance =
              Number.isFinite(rawTotal) && rawTotal > 0
                ? rawTotal
                : hasSimBalance
                ? simBalance
                : rawTotal;
            const simOpenOrders = Number(
              user?.bot?.sim_open_orders ?? user?.bot?.open_orders_count ?? 0
            );
            const openOrdersCount =
              openOrders.length || (isSim && simOpenOrders > 0 ? simOpenOrders : 0);
            const credentialMeta = getCredentialMeta(
              user?.credential_status,
              user?.error,
              user?.pionex_configured,
              tradeMode
            );
            const botMeta = getBotMeta(user?.bot?.status, botEnabled);
            const subMeta = buildSubscriptionMeta(user);
            const chatIds = formatChatIds(user?.telegram_chat_ids);
            const cardState = getCardState(botEnabled, user);
            const gate = user?.gate_prediccion_neto;
            const gateTone = worstGateTone(gate);

            return (
              <article
                key={`${user.username}-${idx}`}
                className={`admin-user-card state-${cardState}${isSim ? " is-sim" : ""}`}
              >
                <header className="admin-user-card-head">
                  <div className="admin-user-card-id">
                    <div className="admin-user-card-title-row">
                      <span className="admin-user-email">{user.username}</span>
                      <span className={`admin-users-mode-chip ${isSim ? "is-sim" : "is-real"}`}>
                        {tradeMode}
                      </span>
                    </div>
                    <small className="muted">{pairsLabel}</small>
                  </div>
                  <div className="admin-user-card-balance">
                    <span className="admin-user-card-balance-label">
                      {isSim ? "Saldo SIM" : "Saldo USDT"}
                    </span>
                    <strong>{formatCurrency(totalBalance, 2)}</strong>
                  </div>
                </header>

                <div className="admin-user-card-badge-row">
                  <span className={`history-reason-badge tone-${botMeta.tone}`}>
                    {botStatusIcon(botMeta.label)} {botMeta.label}
                  </span>
                  <span className={`history-reason-badge tone-${credentialMeta.tone}`}>
                    {credentialMeta.label}
                  </span>
                  <span className={`history-reason-badge tone-${subMeta.tone}`}>
                    {subMeta.badge}
                  </span>
                  {subMeta.pending && typeof onMarkPaid === "function" ? (
                    <button
                      type="button"
                      className="history-action-btn admin-user-mark-paid"
                      title="Marca el período como pagado (medio físico)"
                      onClick={() => onMarkPaid(user)}
                    >
                      Marcar pago
                    </button>
                  ) : null}
                </div>

                {subMeta.detail ? (
                  <div className="admin-user-card-sub-detail muted">{subMeta.detail}</div>
                ) : null}

                {user?.error ? <div className="admin-user-error">{user.error}</div> : null}

                <div className="admin-user-card-stats">
                  <button type="button" className="admin-user-card-kv admin-user-card-kv--link" onClick={() => setPairsModalUser(user)}>
                    <span>Pares</span>
                    <strong>
                      {pairCount > 0 ? `${pairCount} configurados` : "Ver pares"}
                      <i>›</i>
                    </strong>
                  </button>
                  <div className="admin-user-card-kv">
                    <span>Órdenes</span>
                    <strong>{openOrdersCount || 0}</strong>
                  </div>
                  <div className="admin-user-card-kv">
                    <span>Gate neto</span>
                    <strong className={gateTone ? `tone-text-${gateTone}` : ""}>
                      {gate ? `${gate.pasaron ?? 0}/${gate.evaluados ?? 0}` : "—"}
                    </strong>
                  </div>
                  <div className="admin-user-card-kv">
                    <span>Último log</span>
                    <strong className="mono">{formatTime(user?.bot?.last_log_at)}</strong>
                  </div>
                  <div className="admin-user-card-kv">
                    <span>Telegram</span>
                    {chatIds ? (
                      <strong className="mono">{chatIds}</strong>
                    ) : (
                      <strong>
                        <button type="button" className="history-action-btn" onClick={() => onEdit?.(user)}>
                          Agregar
                        </button>
                      </strong>
                    )}
                  </div>
                  <div className="admin-user-card-kv">
                    <span>Billing day</span>
                    <strong>{user?.subscription_billing_day || "—"}</strong>
                  </div>
                </div>

                <section className="admin-user-card-section">
                  <h4>Órdenes abiertas {openOrdersCount > 0 ? `(${openOrdersCount})` : ""}</h4>
                  {openOrders.length ? (
                    <div className="admin-user-order-chips">
                      {openOrders.map((order, orderIdx) => {
                        const orderId = order?.order_id || order?._id || order?.orderId || `o-${orderIdx}`;
                        const side = String(order?.side || "—").toUpperCase();
                        const orderSize = Number(order?.size) || 0;
                        const orderPrice = Number(order?.price) || 0;
                        const orderValue = orderSize * orderPrice;
                        return (
                          <span className={`admin-user-order-chip side-${side.toLowerCase() || "other"}`} key={orderId}>
                            <b>{side}</b>
                            <span>{order?.symbol}</span>
                            <span className="mono">{formatNumber(orderSize, 4)}</span>
                            <span className="mono muted">@ {formatNumber(orderPrice, 6)}</span>
                            {orderValue > 0 ? (
                              <span className="admin-user-order-value">
                                ≈ {formatCurrency(orderValue, 2)} USDT
                              </span>
                            ) : null}
                          </span>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="admin-user-section-empty">
                      {isSim && openOrdersCount > 0 ? `${openOrdersCount} (SIM)` : "Sin órdenes abiertas"}
                    </p>
                  )}
                </section>

                <section className="admin-user-card-section">
                  <h4>Balances</h4>
                  {coins.length ? (
                    <div className="admin-user-coin-chips">
                      {coins.slice(0, 6).map((coin) => (
                        <span className="admin-user-coin-chip" key={`${coin?.coin}-${coin?.symbol || ""}`}>
                          <b>{coin?.coin}</b>
                          {formatCurrency(coin?.usdt_value, 2)}
                        </span>
                      ))}
                      {coins.length > 6 ? (
                        <span className="admin-user-coin-chip muted">+{coins.length - 6} más</span>
                      ) : null}
                    </div>
                  ) : (
                    <p className="admin-user-section-empty">
                      {isSim ? "Saldo simulado" : "Sin balances"} · {formatCurrency(totalBalance, 2)}
                    </p>
                  )}
                </section>

                <footer className="admin-user-card-actions">
                  <button
                    type="button"
                    className="admin-user-card-action"
                    onClick={() => onEdit?.(user)}
                  >
                    Editar
                  </button>
                  <button
                    type="button"
                    className="admin-user-card-action"
                    onClick={() => onConsole?.(user)}
                  >
                    Console
                  </button>
                  {botEnabled ? (
                    <button
                      type="button"
                      className="admin-user-card-action tone-negative"
                      onClick={() => onStop?.(user)}
                    >
                      Inactivar
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="admin-user-card-action tone-positive"
                      onClick={() => onStart?.(user)}
                      disabled={!canStart}
                    >
                      Activar
                    </button>
                  )}
                  <button
                    type="button"
                    className="admin-user-card-action tone-negative"
                    onClick={() => onRestart?.(user)}
                  >
                    Reiniciar
                  </button>
                  <button
                    type="button"
                    className="admin-user-card-action tone-negative"
                    onClick={() => onDelete?.(user?.username)}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Eliminando..." : "Eliminar"}
                  </button>
                </footer>
              </article>
            );
          })}
          </div>

          {totalPages > 1 ? (
            <div className="admin-users-pagination">
              <button
                type="button"
                className="admin-users-page-btn"
                onClick={() => goToPage(safePage - 1)}
                disabled={safePage <= 1}
              >
                ‹
              </button>
              <span className="admin-users-page-info">
                Página <strong>{safePage}</strong> de {totalPages}
              </span>
              <button
                type="button"
                className="admin-users-page-btn"
                onClick={() => goToPage(safePage + 1)}
                disabled={safePage >= totalPages}
              >
                ›
              </button>
            </div>
          ) : null}
        </>
      )}

      <AdminUserPairsModal
        open={modalOpen}
        user={selectedModalUser}
        onClose={() => setPairsModalUser(null)}
        onApplyReplacement={onApplyReplacement}
        onApplyAddition={onApplyAddition}
        onRemovePair={onRemovePair}
        onApplyAllSuggestions={onApplyAllSuggestions}
      />
    </section>
  );
};

export default AdminUsersOverview;